import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, Chip, IconButton, MenuItem, Paper, Skeleton, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography, alpha,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  useGetPendingMakeupsQuery, useMarkMakeupNotRequiredMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm } from "../../../Components/Confirm";

// ── Step Up palette (matches other admin surfaces) ──
const NAVY = "#1b3a6b";
const INK = "#0f172a";
const MUTED = "#64748b";
const HAIRLINE = "#e2e8f0";
const PAGE_BG = "#f8fafc";

const fmtTime = (t?: string) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const d = new Date(); d.setHours(h, m || 0);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(d);
};

const fmtDateShort = (iso?: string) => (iso ? dayjs(iso).format("MMM D, YYYY") : "");

// Age-of-pending chip meta — kept semantically identical to the previous
// implementation (red at 30+, amber at 14+, otherwise blue), just re-skinned.
const ageChip = (days?: number | null) => {
  if (days == null) return { label: "—", color: MUTED, bg: alpha(MUTED, 0.12) };
  if (days >= 30) return { label: `Pending 30+ days`, color: "#b91c1c", bg: alpha("#b91c1c", 0.12) };
  if (days >= 14) return { label: `Pending ${days} days`, color: "#c2410c", bg: alpha("#c2410c", 0.12) };
  return { label: days === 0 ? "Pending today" : `Pending ${days} day${days === 1 ? "" : "s"}`, color: "#0369a1", bg: alpha("#0369a1", 0.1) };
};

// One canonical mode mapping is used by the filter dropdown, the query
// payload, the summary counts, and the active-filter chip. This mirrors
// App\Support\StudyMode on the backend, which is exactly the vocabulary
// listPendingMakeups now canonicalises against, so a client-side pick
// always matches the whole spelling family the backend has stored (see
// backend commit alongside this file).
type ModeBucket = "in_person" | "online" | "hybrid" | "other";
const bucketMode = (m?: string | null): ModeBucket => {
  const v = (m || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
  if (["online", "virtual", "remote"].includes(v)) return "online";
  if (["offline", "in_person", "inperson", "physical", "onsite"].includes(v)) return "in_person";
  if (["hybrid", "blended"].includes(v)) return "hybrid";
  return "other";
};

// User-facing labels — never expose backend spellings like "Offline".
const MODE_LABEL: Record<Exclude<ModeBucket, "other">, string> = {
  in_person: "In-Person",
  online:    "Online",
  hybrid:    "Hybrid",
};
const modeLabel = (m?: string | null): string => {
  const b = bucketMode(m);
  return b === "other" ? (m || "—") : MODE_LABEL[b];
};

// Filter dropdown options — the value is the canonical token sent to
// the backend, the label is what the user sees. Kept as an array so the
// dropdown and the active-filter chip cannot drift.
const MODE_OPTIONS: { value: Exclude<ModeBucket, "other">; label: string }[] = [
  { value: "in_person", label: "In-Person" },
  { value: "online",    label: "Online" },
  { value: "hybrid",    label: "Hybrid" },
];

// ── Small stat cell for the summary strip ─────────────────────────────
const StatCell: React.FC<{ label: string; value: React.ReactNode; icon: React.ReactNode; accent?: string }> = ({
  label, value, icon, accent,
}) => (
  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 1.75, py: 1.25, flex: "1 1 160px", minWidth: 140 }}>
    <Box
      sx={{
        width: 32, height: 32, borderRadius: "10px",
        bgcolor: alpha(accent || NAVY, 0.1),
        color: accent || NAVY,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 800, color: INK, lineHeight: 1.1 }}>{value ?? 0}</Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
    </Box>
  </Stack>
);

/**
 * Centralized queue of pending makeup requests — the actionable list of
 * "we owe this student a replacement class" items across the tenant. Filters
 * mirror the ones supported by /class-schedule/pending-makeups; the row action
 * routes to the student profile's Pending Makeups tab where the scheduling
 * flow lives.
 *
 * This file is UI-only — the RTK query, the mark-not-required mutation, the
 * navigation targets and the filter shape are unchanged from the prior
 * implementation. The visual redesign wraps the same state.
 */
const PendingMakeupsQueue: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();
  const [mode, setMode] = useState("");
  const [from, setFrom] = useState<Dayjs | null>(null);
  const [to, setTo] = useState<Dayjs | null>(null);
  // "original" (the missed-class date) or "makeup" (the scheduled makeup
  // date). Default is original for continuity, but a user filtering by a
  // future date almost certainly means the makeup date — switching this
  // toggle keeps the same From/To values and re-runs the query with the
  // other field. Fixes the reported "created makeup for next Monday not
  // filtering" bug when the user picks Makeup date.
  const [dateField, setDateField] = useState<"original" | "makeup">("original");
  // Backend returns pending_scheduling + scheduled together by default and
  // additionally accepts `no_longer_required` when the UI narrows to the
  // Archived view. The three canonical UI options are: default (actionable),
  // Awaiting scheduling, Scheduled, Archived. The backend whitelist mirrors
  // this exact set — see ClassScheduleController::listPendingMakeups.
  const [statusFilter, setStatusFilter] =
    useState<"" | "pending_scheduling" | "scheduled" | "no_longer_required">("");

  const params = useMemo(
    () => ({
      mode: mode || undefined,
      from_date: from ? from.format("YYYY-MM-DD") : undefined,
      to_date:   to   ? to.format("YYYY-MM-DD")   : undefined,
      date_field: dateField,
      status: statusFilter || undefined,
    }),
    [mode, from, to, dateField, statusFilter]
  );
  const { data, isFetching, isError, refetch } = useGetPendingMakeupsQuery(params);
  const [markNotRequired] = useMarkMakeupNotRequiredMutation();
  const rows: any[] = Array.isArray(data?.data) ? data!.data : [];

  const anyFilter = !!(mode || from || to || statusFilter);
  const resetFilters = () => {
    setMode(""); setFrom(null); setTo(null);
    setDateField("original"); setStatusFilter("");
  };

  // Client-side breakdown — pending vs scheduled + the mode split. Cheap
  // pass over the already-loaded rows; no extra API.
  const counts = useMemo(() => {
    const c = { total: rows.length, pending: 0, scheduled: 0, in_person: 0, online: 0, hybrid: 0 };
    for (const r of rows) {
      if (r.status === "scheduled") c.scheduled++;
      else if (r.status === "pending_scheduling") c.pending++;
      const b = bucketMode(r.original_mode);
      if (b === "in_person") c.in_person++;
      else if (b === "online") c.online++;
      else if (b === "hybrid") c.hybrid++;
    }
    return c;
  }, [rows]);

  const dismiss = async (row: any) => {
    const ok = await confirm({
      title: "No longer required?",
      message: `Mark the pending makeup for ${row.student_name || "this student"} on ${row.original_date ? dayjs(row.original_date).format("MMM D, YYYY") : "the original date"} as no longer required?`,
      confirmText: "Mark No Longer Required",
      variant: "warning",
    });
    if (!ok) return;
    try {
      const res = await markNotRequired({ id: row.id }).unwrap();
      if (res.status) { showMessage(res.message || "Pending makeup dismissed."); refetch(); }
      else showErrorMessage(res.message || "Failed to update.");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to update."); }
  };

  // Human date range for the active-filters chip strip. Dayjs values are
  // formatted through fmtDateShort's ISO path so the chip reads naturally
  // ("Sep 21 – Sep 30, 2026") regardless of which date field they target;
  // the field is signalled with a "(Makeup)" suffix so a user reading a
  // chip knows what they're filtering by.
  const dateRangeChipLabel = (() => {
    const suffix = dateField === "makeup" ? " (Makeup)" : "";
    const f = from ? from.format("YYYY-MM-DD") : "";
    const t = to   ? to.format("YYYY-MM-DD")   : "";
    if (f && t) return `${fmtDateShort(f)} – ${fmtDateShort(t)}${suffix}`;
    if (f)      return `From ${fmtDateShort(f)}${suffix}`;
    if (t)      return `Until ${fmtDateShort(t)}${suffix}`;
    return "";
  })();

  const countBadgeBg = counts.total > 0 ? alpha("#f59e0b", 0.15) : alpha(MUTED, 0.12);
  const countBadgeColor = counts.total > 0 ? "#b45309" : MUTED;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: PAGE_BG, minHeight: "100%" }}>
      {/* ═══ Header card ═══════════════════════════════════════════════════ */}
      <Card variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3, borderColor: HAIRLINE }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "12px",
                bgcolor: alpha("#f59e0b", 0.12), color: "#b45309",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <EventBusyOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK, lineHeight: 1.2 }}>
                  Pending Makeups
                </Typography>
                <Chip
                  size="small"
                  label={counts.total}
                  sx={{
                    height: 22, fontWeight: 800, fontSize: 12,
                    bgcolor: countBadgeBg,
                    color: countBadgeColor,
                    border: `1px solid ${alpha(countBadgeColor, 0.24)}`,
                    "& .MuiChip-label": { px: 0.9 },
                  }}
                />
              </Stack>
              <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
                Review and manage missed-class makeup requests and scheduled makeup follow-ups.
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.4, opacity: 0.85 }}>
                {counts.pending} awaiting scheduling · {counts.scheduled} scheduled
              </Typography>
            </Box>
          </Stack>

          <Tooltip title={isFetching ? "Refreshing…" : "Refresh"}>
            <span>
              <IconButton
                onClick={() => refetch()}
                disabled={isFetching}
                size="small"
                aria-label="Refresh"
                sx={{
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 2,
                  color: INK,
                  "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
                }}
              >
                <RefreshOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Card>

      {/* ═══ Summary strip ═══════════════════════════════════════════════ */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 3, borderColor: HAIRLINE, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex", flexWrap: "wrap",
            "& > *": {
              borderRight: { md: `1px solid ${HAIRLINE}` },
              borderBottom: { xs: `1px solid ${HAIRLINE}`, md: "none" },
            },
            "& > *:last-of-type": { borderRight: "none", borderBottom: "none" },
          }}
        >
          <StatCell label="Awaiting"  value={counts.pending}   icon={<PendingActionsOutlinedIcon fontSize="small" />} accent="#b45309" />
          <StatCell label="Scheduled" value={counts.scheduled} icon={<EventAvailableOutlinedIcon fontSize="small" />} accent="#16a34a" />
          <StatCell label="In-Person" value={counts.in_person} icon={<PlaceOutlinedIcon fontSize="small" />}         accent="#8b5cf6" />
          <StatCell label="Online"    value={counts.online}    icon={<LaptopMacOutlinedIcon fontSize="small" />}     accent="#0ea5e9" />
          <StatCell label="Hybrid"    value={counts.hybrid}    icon={<HubOutlinedIcon fontSize="small" />}           accent="#f59e0b" />
        </Box>
      </Card>

      {/* ═══ Filter card ═══════════════════════════════════════════════════ */}
      <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 3, borderColor: HAIRLINE }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          alignItems={{ md: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            size="small" select label="Mode" value={mode}
            onChange={(e) => setMode(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All modes</MenuItem>
            {MODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" select label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="pending_scheduling">Awaiting scheduling</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="no_longer_required">Archived</MenuItem>
          </TextField>
          {/* Date-field toggle — the same From/To can target either the
              missed-class date (default) or the actual scheduled makeup
              date. Prevents the "created makeup for next Monday not
              found" case where filtering only made sense against original
              date. Backend accepts `date_field=original|makeup`. */}
          <ToggleButtonGroup
            size="small" exclusive value={dateField}
            onChange={(_, v) => v && setDateField(v)}
            aria-label="Date filter target"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none", fontSize: 12, fontWeight: 700,
                px: 1.5, py: 0.5, borderColor: HAIRLINE, color: MUTED,
                "&.Mui-selected": {
                  bgcolor: alpha(NAVY, 0.08), color: NAVY,
                  borderColor: alpha(NAVY, 0.35),
                  "&:hover": { bgcolor: alpha(NAVY, 0.12) },
                },
              },
            }}
          >
            <ToggleButton value="original">Original date</ToggleButton>
            <ToggleButton value="makeup">Makeup date</ToggleButton>
          </ToggleButtonGroup>
          {/* Polished MUI DatePickers replacing the native <input type="date">
              — matches the Class Sessions filter panel look, gives a proper
              calendar popup, keeps the label visible with a healthy 170 px
              min-width so MM/DD/YYYY + calendar icon never clip. */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={dateField === "makeup" ? "From (makeup date)" : "From (original date)"}
              value={from}
              onChange={setFrom}
              slotProps={{ textField: { size: "small", sx: { minWidth: 170 } } }}
            />
            <DatePicker
              label={dateField === "makeup" ? "To (makeup date)" : "To (original date)"}
              value={to}
              onChange={setTo}
              slotProps={{ textField: { size: "small", sx: { minWidth: 170 } } }}
            />
          </LocalizationProvider>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="text"
            onClick={resetFilters}
            disabled={!anyFilter}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: anyFilter ? NAVY : MUTED,
              "&.Mui-disabled": { color: alpha(MUTED, 0.5) },
            }}
          >
            Reset filters
          </Button>
        </Stack>

        {/* Active-filter chips — only visible when at least one filter is set */}
        {anyFilter && (
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${HAIRLINE}` }}
          >
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.05em", mr: 0.5 }}>
              Active filters
            </Typography>
            {mode && (
              <Chip
                size="small"
                label={modeLabel(mode)}
                onDelete={() => setMode("")}
                deleteIcon={<CloseRoundedIcon />}
                sx={{ height: 24, fontSize: 12, fontWeight: 600, bgcolor: alpha(NAVY, 0.06), border: `1px solid ${alpha(NAVY, 0.16)}`, color: NAVY }}
              />
            )}
            {statusFilter && (
              <Chip
                size="small"
                label={
                  statusFilter === "scheduled"           ? "Scheduled" :
                  statusFilter === "no_longer_required"  ? "Archived"  :
                                                           "Awaiting scheduling"
                }
                onDelete={() => setStatusFilter("")}
                deleteIcon={<CloseRoundedIcon />}
                sx={{ height: 24, fontSize: 12, fontWeight: 600, bgcolor: alpha(NAVY, 0.06), border: `1px solid ${alpha(NAVY, 0.16)}`, color: NAVY }}
              />
            )}
            {dateRangeChipLabel && (
              <Chip
                size="small"
                label={dateRangeChipLabel}
                onDelete={() => { setFrom(null); setTo(null); }}
                deleteIcon={<CloseRoundedIcon />}
                sx={{ height: 24, fontSize: 12, fontWeight: 600, bgcolor: alpha(NAVY, 0.06), border: `1px solid ${alpha(NAVY, 0.16)}`, color: NAVY }}
              />
            )}
            <Button size="small" onClick={resetFilters} sx={{ ml: 0.5, textTransform: "none", fontSize: 12, fontWeight: 600 }}>
              Clear all
            </Button>
          </Stack>
        )}
      </Card>

      {/* ═══ Content ═══════════════════════════════════════════════════════ */}
      {isError ? (
        <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, borderColor: HAIRLINE }}>
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
          >
            <Box sx={{ fontWeight: 700 }}>Unable to load pending makeups</Box>
            Please try again.
          </Alert>
        </Card>
      ) : isFetching && rows.length === 0 ? (
        <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, borderColor: HAIRLINE }}>
          <Stack spacing={1}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={54} />
            ))}
          </Stack>
        </Card>
      ) : rows.length === 0 ? (
        <Card variant="outlined" sx={{ py: 6, px: 3, textAlign: "center", borderRadius: 3, borderColor: HAIRLINE }}>
          <Box
            sx={{
              width: 56, height: 56, borderRadius: "50%",
              bgcolor: alpha(anyFilter ? NAVY : "#16a34a", 0.08),
              color: anyFilter ? NAVY : "#16a34a",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              mb: 1.5,
            }}
          >
            <EventAvailableOutlinedIcon fontSize="large" />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
            {anyFilter ? "No requests match these filters" : "No pending makeup requests"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75, mx: "auto", maxWidth: 460 }}>
            {anyFilter
              ? "Try adjusting the mode or original-date range."
              : "There are currently no missed-class makeup requests waiting for action."}
          </Typography>
          {anyFilter && (
            <Button
              variant="outlined"
              size="small"
              onClick={resetFilters}
              sx={{ mt: 2, textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Clear filters
            </Button>
          )}
        </Card>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 3, borderColor: HAIRLINE, overflow: "hidden" }}>
          <Paper elevation={0} sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em", bgcolor: "#fafbfc", borderBottom: `1px solid ${HAIRLINE}`, py: 1.25 } }}>
                  <TableCell>Student</TableCell>
                  <TableCell>Original Class</TableCell>
                  <TableCell>Original Date / Time</TableCell>
                  <TableCell>Makeup Date</TableCell>
                  <TableCell>Staff</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" sx={{ minWidth: 220 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const age = ageChip(r.pending_days);
                  const modeText = modeLabel(r.original_mode);
                  const bucket = bucketMode(r.original_mode);
                  const modeColour =
                    bucket === "online" ? "#0ea5e9" :
                    bucket === "in_person" ? "#8b5cf6" :
                    bucket === "hybrid" ? "#f59e0b" : MUTED;
                  return (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{
                        "& td": { fontSize: 13, borderBottom: `1px solid ${HAIRLINE}`, py: 1.25, verticalAlign: "top" },
                        "&:last-of-type td": { borderBottom: "none" },
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, lineHeight: 1.3 }}>
                          {r.student_name || `Student #${r.student_id}`}
                        </Typography>
                        {r.grade && (
                          <Typography sx={{ fontSize: 11.5, color: MUTED }}>{r.grade}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, color: INK }}>{r.class_name || "—"}</Typography>
                        {!!r.subjects?.length && (
                          <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                            {r.subjects.join(", ")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, color: INK }}>
                          {r.original_date ? dayjs(r.original_date).format("MMM D, YYYY") : "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                          {r.original_start_time ? `${fmtTime(r.original_start_time)} – ${fmtTime(r.original_end_time)}` : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {r.makeup_date ? (
                          <>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CalendarMonthOutlinedIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                              <Typography sx={{ fontSize: 13, color: INK, fontWeight: 600 }}>
                                {dayjs(r.makeup_date).format("MMM D, YYYY")}
                              </Typography>
                            </Stack>
                            <Typography sx={{ fontSize: 11.5, color: MUTED, ml: 2.25 }}>
                              {r.makeup_start_time ? `${fmtTime(r.makeup_start_time)} – ${fmtTime(r.makeup_end_time)}` : "—"}
                            </Typography>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: 12.5, color: MUTED, fontStyle: "italic" }}>
                            Not scheduled
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, color: INK }}>{r.original_staff_name || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={modeText}
                          sx={{
                            height: 22, fontSize: 11.5, fontWeight: 700,
                            bgcolor: alpha(modeColour, 0.1),
                            color: modeColour,
                            border: `1px solid ${alpha(modeColour, 0.22)}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {r.status === "scheduled" ? (
                          <Chip
                            size="small"
                            label="Scheduled"
                            sx={{
                              height: 22, fontWeight: 700, fontSize: 11.5,
                              bgcolor: alpha("#16a34a", 0.12), color: "#166534",
                              border: `1px solid ${alpha("#16a34a", 0.28)}`,
                            }}
                          />
                        ) : r.status === "no_longer_required" ? (
                          <Chip
                            size="small"
                            label="Archived"
                            sx={{
                              height: 22, fontWeight: 700, fontSize: 11.5,
                              bgcolor: alpha(MUTED, 0.14), color: MUTED,
                              border: `1px solid ${alpha(MUTED, 0.3)}`,
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label={age.label}
                            sx={{ height: 22, fontWeight: 700, fontSize: 11.5, bgcolor: age.bg, color: age.color }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {r.status === "no_longer_required" ? (
                          // Archived rows have no forward actions — the record
                          // is terminal; a Schedule button here would either
                          // no-op or (worse) hit backend logic that only
                          // accepts pending rows. Show a muted marker instead
                          // so the column stays visually consistent.
                          <Typography sx={{ fontSize: 12, color: MUTED, fontStyle: "italic" }}>
                            No actions
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={0.75} justifyContent="center">
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<EventRepeatOutlinedIcon fontSize="small" />}
                              onClick={() => navigate(`/student-history/${r.student_id}?tab=PendingMakeups`)}
                              sx={{
                                textTransform: "none", fontWeight: 600, borderRadius: 2,
                                bgcolor: NAVY, boxShadow: "none",
                                "&:hover": { bgcolor: "#152d55", boxShadow: "none" },
                              }}
                            >
                              Schedule
                            </Button>
                            {/* Not Needed only makes sense on rows currently
                                Awaiting scheduling — the backend endpoint
                                (markMakeupNotNeeded) refuses to dismiss a
                                row whose status is not STATUS_PENDING, so we
                                hide the button on scheduled rows rather than
                                render an action that would error on click. */}
                            {r.status === "pending_scheduling" && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<DoneAllRoundedIcon fontSize="small" />}
                                onClick={() => dismiss(r)}
                                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                              >
                                Not Needed
                              </Button>
                            )}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Card>
      )}
    </Box>
  );
};

export default PendingMakeupsQueue;
